import React, { Component } from 'react';
import { Formik, Form, Field } from 'formik';
import { requestSignupOtp, verifyOtpAndCreateUser } from '../../api/sessions';
import history from '../../history';
import { loginUser } from '../../redux/actions';
import * as yup from 'yup';
import { connect } from 'react-redux';
import ErrorMessage from '../ErrorMessage';
import { Link } from 'react-router-dom';
import SocialLinks from './SocialLinks';
import { Seo } from '../shared';
import { textValidate } from '../../helpers';
import { Segment } from 'semantic-ui-react';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 60;

class SignUpForm extends Component {
	constructor(props) {
		super(props);
		this.state = {
			step: 1,
			loading: false,
			email: '',
			otpValues: Array(OTP_LENGTH).fill(''),
			resendCooldown: 0
		};
		this.otpRefs = [];
		this.resendTimer = null;
	}

	componentWillUnmount() {
		if (this.resendTimer) clearInterval(this.resendTimer);
	}

	setLoading = (value) => this.setState({ loading: value });

	startResendCooldown = () => {
		this.setState({ resendCooldown: RESEND_COOLDOWN_SEC });
		this.resendTimer = setInterval(() => {
			this.setState((s) => {
				if (s.resendCooldown <= 1) {
					clearInterval(this.resendTimer);
					return { resendCooldown: 0 };
				}
				return { resendCooldown: s.resendCooldown - 1 };
			});
		}, 1000);
	};

	handleSendCode = (email) => {
		this.setState({ status: null });
		this.setLoading(true);
		requestSignupOtp(email)
			.then(() => {
				this.setState({ step: 2, email: email.trim().toLowerCase(), loading: false });
				this.startResendCooldown();
			})
			.catch((err) => {
				this.setLoading(false);
				const msg = err?.response?.data?.errors;
				this.setState({ status: Array.isArray(msg) ? msg.join(' ') : msg || 'Failed to send code. Try again.' });
			});
	};

	handleOtpChange = (index, value, setFieldValue) => {
		if (!/^\d*$/.test(value)) return;
		const next = [...this.state.otpValues];
		next[index] = value.slice(-1);
		this.setState({ otpValues: next });
		setFieldValue('otp', next.join(''));
		if (value && index < OTP_LENGTH - 1) this.otpRefs[index + 1]?.focus();
	};

	handleOtpKeyDown = (index, e, setFieldValue) => {
		if (e.key === 'Backspace' && !this.state.otpValues[index] && index > 0) {
			this.otpRefs[index - 1]?.focus();
			const next = [...this.state.otpValues];
			next[index - 1] = '';
			this.setState({ otpValues: next });
			setFieldValue('otp', next.join(''));
		}
	};

	handleOtpPaste = (e, setFieldValue) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
		const next = [...this.state.otpValues];
		pasted.forEach((char, i) => { next[i] = char; });
		this.setState({ otpValues: next });
		setFieldValue('otp', next.join(''));
		const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
		this.otpRefs[focusIdx]?.focus();
	};

	handleResend = () => {
		if (this.state.resendCooldown > 0) return;
		this.setLoading(true);
		requestSignupOtp(this.state.email)
			.then(() => {
				this.setLoading(false);
				this.startResendCooldown();
			})
			.catch(() => this.setLoading(false));
	};

	setStatus = (msg) => this.setState({ status: msg });

	render() {
		const { loading, step, email, otpValues, resendCooldown, status } = this.state;
		// Support ?return=... (e.g. from login when user came from group-trips) so register → login and post-signup redirect go back
		const searchParams = new URLSearchParams(this.props.location?.search || "");
		const returnParam = searchParams.get("return");
		const redirectUrl = (returnParam && returnParam.startsWith("/"))
			? returnParam
			: (this.props.location?.state?.from
				? (typeof this.props.location.state.from === 'string' ? this.props.location.state.from : this.props.location.state.from?.pathname || '/')
				: '/');

		const step1Schema = yup.object().shape({
			email: yup.string().email('Enter a valid email').required('Email is required')
		});

		const step2Schema = yup.object().shape({
			otp: yup.string().length(OTP_LENGTH, `Enter all ${OTP_LENGTH} digits`).required('Verification code is required'),
			name: textValidate(yup).required('Required'),
			password: yup.string().min(6, 'At least 6 characters').required('Required'),
			password_confirmation: yup.string().oneOf([yup.ref('password'), null], "Passwords don't match!").required('Required')
		});

		return (
			<div className="login-page-wrapper full-page">
				<Seo title="Sign Up" path="/signup" noindex />
				<Segment loading={loading} className="login-segment">
					<div className="login-form-card">
						<div className="login-form-card-body">
							<h2 className="login-title">Create account</h2>
							<p className="login-subtitle">Join Visit All Nepal to book tours and rentals</p>

							<div className="signup-google-hero">
								<SocialLinks redirectUrl={redirectUrl} setLoading={this.setLoading} />
							</div>
							<div className="login-divider">
								<span>or create with email</span>
							</div>

							{status && (
								<div className={`signup-error-box mb-3 ${status.toLowerCase().includes('already registered') ? 'signup-error-box--registered' : ''}`} role="alert">
									{status.toLowerCase().includes('already registered') && (
										<div className="signup-error-box-icon">
											<i className="fas fa-exclamation-circle" aria-hidden="true" />
										</div>
									)}
									<div className="signup-error-box-text">{status}</div>
									{status.toLowerCase().includes('already registered') && (
										<Link to={{ pathname: '/login', state: { from: redirectUrl } }} className="btn btn-secondary btn-sm mt-2">
											Sign in
										</Link>
									)}
								</div>
							)}

							{step === 1 && (
								<Formik
									initialValues={{ email: '' }}
									validationSchema={step1Schema}
									onSubmit={(values) => {
										this.setStatus(null);
										this.handleSendCode(values.email);
									}}
								>
									{({ values, handleChange, handleBlur, handleSubmit, errors, touched, setStatus }) => (
										<form onSubmit={handleSubmit} className="form-wrap mt-3">
											<div className="field">
												<label>Email</label>
												<Field
													type="email"
													name="email"
													className="form-control"
													placeholder="you@example.com"
													onBlur={handleBlur}
													onChange={(e) => { handleChange(e); setStatus(null); this.setStatus(null); }}
													value={values.email}
												/>
												<ErrorMessage name="email" />
											</div>
											<button className="btn btn-secondary my-2 w-100" type="submit" disabled={loading}>
												Send verification code
											</button>
											<p className="login-create-account-footer mt-3 mb-0">
												Already have an account? <Link to={{ pathname: '/login', state: { from: redirectUrl } }}>Sign in</Link>
											</p>
										</form>
									)}
								</Formik>
							)}

							{step === 2 && (
								<Formik
									initialValues={{ otp: '', name: '', password: '', password_confirmation: '' }}
									validationSchema={step2Schema}
									onSubmit={(values, { setSubmitting, setStatus }) => {
										this.setStatus(null);
										this.setLoading(true);
										verifyOtpAndCreateUser({
											email,
											otp: values.otp,
											name: values.name,
											password: values.password,
											password_confirmation: values.password_confirmation
										})
											.then((response) => {
												this.props.loginUser(response.data.user);
												setSubmitting(false);
												history.push(redirectUrl);
											})
											.catch((err) => {
												this.setLoading(false);
												const msg = err?.response?.data?.errors;
												setStatus(Array.isArray(msg) ? msg.join(' ') : msg || 'Verification failed. Try again.');
											});
									}}
								>
									{({ values, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting, status: formStatus }) => (
										<form onSubmit={handleSubmit} className="form-wrap mt-3">
											<p className="signup-otp-sent-msg">
												We sent a 6-digit code to <strong>{email}</strong>
											</p>
											<button
												type="button"
												className="btn-link p-0 border-0 bg-transparent text-secondary small mb-2"
												onClick={() => this.setState({ step: 1, status: null, otpValues: Array(OTP_LENGTH).fill('') })}
											>
												Change email
											</button>

											<div className="field">
												<label>Verification code</label>
												<div className="otp-input-wrap">
													{otpValues.map((v, i) => (
														<input
															key={i}
															ref={(el) => { this.otpRefs[i] = el; }}
															type="text"
															inputMode="numeric"
															maxLength={1}
															className="otp-input-box"
															value={v}
															onChange={(e) => this.handleOtpChange(i, e.target.value, setFieldValue)}
															onKeyDown={(e) => this.handleOtpKeyDown(i, e, setFieldValue)}
															onPaste={(e) => this.handleOtpPaste(e, setFieldValue)}
														/>
													))}
												</div>
												<ErrorMessage name="otp" />
											</div>

											<div className="field">
												<label>Full name</label>
												<Field type="text" name="name" className="form-control" placeholder="Full name" onBlur={handleBlur} onChange={handleChange} value={values.name} />
												<ErrorMessage name="name" />
											</div>
											<div className="field">
												<label>Password</label>
												<Field type="password" name="password" className="form-control" placeholder="Min 6 characters" onBlur={handleBlur} onChange={handleChange} value={values.password} />
												<ErrorMessage name="password" />
											</div>
											<div className="field">
												<label>Confirm password</label>
												<Field type="password" name="password_confirmation" className="form-control" placeholder="Confirm password" onBlur={handleBlur} onChange={handleChange} value={values.password_confirmation} />
												<ErrorMessage name="password_confirmation" />
											</div>

											{(formStatus || status) && (
												<div className="alert alert-danger mb-3" role="alert">
													{formStatus || status}
												</div>
											)}

											<button className="btn btn-secondary my-2 w-100" type="submit" disabled={isSubmitting || loading}>
												Verify and create account
											</button>

											<div className="text-center mt-2">
												{resendCooldown > 0 ? (
													<span className="text-muted small">Resend code in {resendCooldown}s</span>
												) : (
													<button type="button" className="btn-link p-0 border-0 bg-transparent small" onClick={this.handleResend}>
														Resend code
													</button>
												)}
											</div>

											<p className="login-create-account-footer mt-3 mb-0">
												Already have an account? <Link to={{ pathname: '/login', state: { from: redirectUrl } }}>Sign in</Link>
											</p>
										</form>
									)}
								</Formik>
							)}
						</div>
					</div>
				</Segment>
			</div>
		);
	}
}

const mapStateToProps = ({ currentUser }) => ({ currentUser });
const mapDispatchToProps = { loginUser };

export default connect(mapStateToProps, mapDispatchToProps)(SignUpForm);
